/**
 * Resumable multi-step saves.
 *
 * Several "create" flows write to more than one place: a cloud function plus a Firestore
 * document, or several Firestore documents. When a later step fails, the earlier ones are
 * already persisted. Re-running the whole flow then fails with "already exists" while the
 * record stays half-created (a login account without a dashboard profile, an assistant
 * without its initial version).
 *
 * `SaveSteps` remembers which steps completed, so calling `run()` again only executes the
 * steps that are still pending. Each step is retried on transient errors before giving up.
 */

export interface SaveStep {
  /** Stable identifier used to remember completion across runs. */
  key: string;
  /** Describes the step in error messages, e.g. "creating the login account". */
  label: string;
  run: () => Promise<unknown>;
}

/** Thrown by `SaveSteps.run()`; `reason` is the error the failing step rejected with. */
class SaveStepError extends Error {
  constructor(readonly step: SaveStep, readonly reason: unknown) {
    super(`${capitalize(step.label)} failed: ${errorMessage(reason)}`);
    this.name = 'SaveStepError';
  }
}

/** The message to show the user: the underlying failure, without the step wrapper. */
export function failureMessage(error: unknown): string {
  return errorMessage(error instanceof SaveStepError ? error.reason : error);
}

export class SaveSteps {
  private readonly completed = new Set<string>();
  private failed: SaveStep | null = null;

  constructor(private readonly steps: readonly SaveStep[]) {
    const keys = new Set(steps.map(step => step.key));
    if (keys.size !== steps.length) {
      throw new Error('SaveSteps: step keys must be unique');
    }
  }

  /** True once at least one step has been persisted, so the next `run()` resumes instead of starting over. */
  get hasProgress(): boolean {
    return this.completed.size > 0;
  }

  /** The step that made the last `run()` fail, or null after a successful run. */
  get failedStep(): SaveStep | null {
    return this.failed;
  }

  isCompleted(key: string): boolean {
    return this.completed.has(key);
  }

  /**
   * Runs every step that has not completed yet, in order. Stops at the first step that
   * still fails after its retries and throws a `SaveStepError` naming it. Calling `run()`
   * again resumes from that step.
   */
  async run(): Promise<void> {
    for (const step of this.steps) {
      if (this.completed.has(step.key)) {
        continue;
      }
      try {
        await retry(step.run);
      } catch (reason) {
        this.failed = step;
        throw new SaveStepError(step, reason);
      }
      this.completed.add(step.key);
    }
    this.failed = null;
  }
}

/** Total attempts per step, including the first one. */
const ATTEMPTS = 3;
/** Delay before the second attempt; it doubles for every following attempt. */
const INITIAL_DELAY_MS = 500;

/**
 * Error codes (Firestore, Firebase Auth, callable functions) for which another attempt
 * with the same input cannot succeed. Codes are compared without their `auth/` or
 * `functions/` prefix.
 */
const PERMANENT_ERROR_CODES = new Set([
  'already-exists',
  'cancelled',
  'email-already-exists',
  'failed-precondition',
  'invalid-argument',
  'invalid-email',
  'invalid-password',
  'not-found',
  'out-of-range',
  'permission-denied',
  'unauthenticated',
  'unimplemented',
]);

async function retry<T>(operation: () => Promise<T>): Promise<T> {
  let delayMs = INITIAL_DELAY_MS;

  for (let attempt = 1; ; attempt++) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= ATTEMPTS || !isRetryableError(error)) {
        throw error;
      }
      await sleep(delayMs);
      delayMs *= 2;
    }
  }
}

/** Network glitches and backend hiccups are retried; conflicts and permission problems are not. */
function isRetryableError(error: unknown): boolean {
  const code = errorCode(error);
  if (code !== null && PERMANENT_ERROR_CODES.has(code)) {
    return false;
  }
  return !/already exists/i.test(errorMessage(error));
}

function errorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const code = (error as { code?: unknown }).code;
  if (typeof code !== 'string') {
    return null;
  }
  return code.slice(code.lastIndexOf('/') + 1).toLowerCase();
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return String(error);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
